import isObject from 'lodash/isObject'
import isNumber from 'lodash/isNumber'

// 平移动画及时长
const MOVE_EASING = 'linear'
const MOVE_SPEED = 200

const ZOOM_EASING = 'linear'
const ZOOM_SPEED = 150

/**
 * 计算 x 轴 label 是否显示
 * @param label
 * @param labels
 * @param coord
 * @param resize
 * @param showAllLabel
 * @return {boolean}
 */
const calculateXLabelState = (label, labels, coord, resize = false, showAllLabel = false) => {
  const sortedLabels = [...labels].sort((a, b) => a.get('value') - b.get('value'))
  const { x } = coord

  const currentMatrix = label.getMatrix()
  const attrs = label.get('attrs')

  // 线类型没有 x 坐标，有 x1, x2
  const position = currentMatrix[4] + (isNumber(attrs.x) ? attrs.x : attrs.x1)

  if (resize) {
    let shouldShow = true
    if (!showAllLabel) {
      const index = sortedLabels.indexOf(label)
      // 第一个不需要检查前面是否被覆盖
      if (index > 0) {
        const { width } = label.getBBox()

        let preIndex = index
        let preLabel
        let prevVisible

        do {
          preIndex--
          preLabel = sortedLabels[preIndex]
          prevVisible = preLabel.get('shouldVisible')

          const preMatrix = preLabel.getMatrix()
          const preAttrs = preLabel.get('attrs')
          const prePosition = preAttrs.x + preMatrix[4]
          const box = preLabel.getBBox()

          shouldShow = !prevVisible || ((position - (width / 2)) - (prePosition + (box.width / 2))) >= 8

          if (!shouldShow && index === labels.length - 1) {
            shouldShow = true
            preLabel.set('shouldVisible', false)
            preLabel.hide()
          }
        } while (preIndex >= 1 && shouldShow)
      }
    }

    label.set('shouldVisible', shouldShow)
  }

  // 首先检查边界
  return position >= x.start && position <= x.end && label.get('shouldVisible')
}
/**
 * 计算 y 轴 label 是否显示
 * @param label
 * @param labels
 * @param coord
 * @param resize
 * @param showAllLabel
 * @return {boolean}
 */
const calculateYLabelState = (label, labels, coord, resize = false, showAllLabel = false) => {
  const sortedLabels = [...labels].sort((a, b) => a.get('value') - b.get('value'))
  const { y } = coord

  const currentMatrix = label.getMatrix()
  const attrs = label.get('attrs')

  // 线类型没有 y 坐标，有 y1, y2
  const position = currentMatrix[5] + (isNumber(attrs.y) ? attrs.y : attrs.y1)

  if (resize) {
    let shouldShow = true
    if (!showAllLabel) {
      const index = sortedLabels.indexOf(label)
      // 第一个不需要检查前面是否被覆盖
      if (index > 0) {
        const { height } = label.getBBox()

        let preIndex = index
        let preLabel
        let prevVisible

        do {
          preIndex--
          preLabel = sortedLabels[preIndex]
          prevVisible = preLabel.get('shouldVisible')

          const preMatrix = preLabel.getMatrix()
          const preAttrs = preLabel.get('attrs')
          const prePosition = preAttrs.y + preMatrix[5]
          const box = preLabel.getBBox()

          shouldShow = !prevVisible || (prePosition - (box.height / 2)) - (position + (height / 2)) >= 0
          // console.log(prevVisible, index, preIndex, (prePosition - (box.height / 2)) - ((position + (height / 2))), position, prePosition, height, box.height)
          if (!shouldShow && index === labels.length - 1) {
            shouldShow = true
            preLabel.set('shouldVisible', false)
            preLabel.hide()
          }
        } while (preIndex >= 1 && shouldShow)
      }
    }

    label.set('shouldVisible', shouldShow)
  }

  // 首先检查边界
  return position >= y.end && position <= y.start && label.get('shouldVisible')
}
/**
 * 移动垂直指标器
 * @param shape
 * @param translateX
 * @param coord
 * @param cb
 * @param easing
 * @param duration
 */
const moveCrosshairsShapeY = (shape, translateX, coord, cb, easing = MOVE_EASING, duration = MOVE_SPEED) => {
  if (!shape) return

  const { x } = coord
  const crossMatrix = [...shape.getMatrix()]
  crossMatrix[4] += (crossMatrix[0] * translateX)

  const finalMatrix = [...crossMatrix]

  shape.animate().to({
    attrs: {
      matrix: crossMatrix,
    },
    duration,
    easing,
  }).onUpdate(() => {
    const currentMatrix = shape.getMatrix()
    if (currentMatrix[4] >= x.start && currentMatrix[4] <= x.end) {
      shape.show()
    } else {
      shape.hide()
    }
  }).onEnd(() => {
    shape.setMatrix(finalMatrix)
  })
}

/**
 * 移动水平指标器
 * @param shape
 * @param translateY
 * @param coord
 * @param cb
 * @param easing
 * @param duration
 */
const moveCrosshairsShapeX = (shape, translateY, coord, cb, easing = MOVE_EASING, duration = MOVE_SPEED) => {
  if (!shape) return

  const { y } = coord
  const crossMatrix = [...shape.getMatrix()]
  crossMatrix[5] += (crossMatrix[3] * translateY)

  const finalMatrix = [...crossMatrix]

  shape.animate().to({
    attrs: {
      matrix: crossMatrix,
    },
    duration,
    easing,
  }).onUpdate(() => {
    const currentMatrix = shape.getMatrix()
    if (currentMatrix[5] >= y.end && currentMatrix[5] <= y.start) {
      shape.show()
    } else {
      shape.hide()
    }
  }).onEnd(() => {
    shape.setMatrix(finalMatrix)
  })
}

const calculateLabelState = (direction, shape, shapes, coord, resizeLabel, showAllLabel) => {
  let y = true
  let x = true
  if (direction.indexOf('y') !== -1) {
    y = calculateYLabelState(shape, shapes, coord, resizeLabel, showAllLabel)
  }
  if (direction.indexOf('x') !== -1) {
    x = calculateXLabelState(shape, shapes, coord, resizeLabel, showAllLabel)
  }
  return x && y
}

const changeLabelText = (chart, label) => {
  if (label.get('textChanged')) {
    return
  }
  const data = chart.get('data')
  const text = label.attr('text')
  const itemData = data.find(d => d.name === text)
  if (itemData && itemData.labelWidth > itemData.maxLabelWidth && itemData.transText) {
    label.attr('text', itemData.transText)
    label.set('textChanged', true)
  }
}

/**
 * 移动坐标标签
 * @param shape
 * @param shapes
 * @param translateX
 * @param coord
 * @param showAllLabel
 * @param isLine 线的计算是否显示方法与 rect 不一致
 * @param animateConfig
 * @param resizeLabel
 * @param isValueLabel
 */
const moveLabel = (
  chart,
  shape,
  shapes,
  translateX,
  { resizeLabel, isValueLabel, showAllLabel } = { resizeLabel: false, isValueLabel: false, showAllLabel: false },
  animateConfig = { easing: MOVE_EASING, duration: MOVE_SPEED },
  direction = 'x',
) => {
  const coord = chart.get('coord')
  
  if (!shape) return
  const shapeMatrix = [...shape.getMatrix()]
  if (direction.indexOf('y') !== -1) {
    shapeMatrix[5] += (shapeMatrix[3] * translateX)
  }
  if (direction.indexOf('x') !== -1) {
    shapeMatrix[4] += (shapeMatrix[0] * translateX)
  }
  if (shape.get('type') === 'text') {
    changeLabelText(chart, shape)
  }

  const finalMatrix = [...shapeMatrix]
  // 没有动画时直接设置
  if (!animateConfig) {
    shape.setMatrix(shapeMatrix)
    const visible = calculateLabelState(direction, shape, shapes, coord, resizeLabel, showAllLabel)
    visible ? shape.show() : shape.hide()
    return
  }

  shape.animate().to({
    attrs: {
      matrix: shapeMatrix,
    },
    ...animateConfig,
  }).onUpdate(() => {
    if (isValueLabel) {
      shape.show()
      return
    }
    // 判断是否超出边界，超出边界不显示
    const visible = calculateLabelState(direction, shape, shapes, coord, resizeLabel, showAllLabel)
    visible ? shape.show() : shape.hide()
  }).onEnd(() => {
    shape.setMatrix(finalMatrix)
  })
}

/**
 * 更新轴标签
 * @param chart 单图
 * @param beforeMatrix 更新前的 geom 转换矩阵
 * @param afterMatrix 更新后的 geom 转换矩阵
 * @param resizeLabel 是否要重新计算标签是否显示
 * @param animateConfig 动画配置
 * @param axisType 轴类型
 */
const updateAxisLabel = (chart, beforeMatrix, afterMatrix, resizeLabel = false, animateConfig = { easing: MOVE_EASING, duration: MOVE_SPEED }, axisType = 'x') => {
  const axisController = chart.get('axisController')

  if (!axisController || !isObject(axisController.axes)) return
  const { axes, axisCfg } = axisController
  const needUpdateAxes = Object.values(axes).filter(d => d && d.dimType === axisType)

  needUpdateAxes.forEach((axis) => {
    const { labels, name, backContainer } = axis
    const { showAllLabel } = (axisCfg && axisCfg[name]) || {}

    labels.sort((a, b) => a._attrs.value - b._attrs.value).forEach((label) => {
      let labelTranslateX = 0
      if (Array.isArray(beforeMatrix) && Array.isArray(afterMatrix)) {
        const labelX = label.get('attrs').x
        labelTranslateX = afterMatrix[4] - beforeMatrix[4] + ((afterMatrix[0] - beforeMatrix[0]) * labelX)
      }
      moveLabel(chart, label, labels, labelTranslateX, { resizeLabel, showAllLabel }, animateConfig)
    })

    const ticks = backContainer.getChildren().filter(d => d._id === 'axis-x-ticks')
    ticks.forEach((tick) => {
      let tickTranslateX = 0
      if (Array.isArray(beforeMatrix) && Array.isArray(afterMatrix)) {
        const tickX = tick.get('attrs').x1
        tickTranslateX = afterMatrix[4] - beforeMatrix[4] + ((afterMatrix[0] - beforeMatrix[0]) * tickX)
      }
      moveLabel(chart, tick, ticks, tickTranslateX, { resizeLabel, showAllLabel: true }, animateConfig)
    })
  })
}

/**
 * 更新轴标签
 * @param chart 单图
 * @param beforeMatrix 更新前的 geom 转换矩阵
 * @param afterMatrix 更新后的 geom 转换矩阵
 * @param resizeLabel 是否要重新计算标签是否显示
 * @param animateConfig 动画配置
 * @param axisType 轴类型
 */
const updateAxisLabelY = (chart, beforeMatrix, afterMatrix, resizeLabel = false, animateConfig = { easing: MOVE_EASING, duration: MOVE_SPEED }, axisType = 'x') => {
  const axisController = chart.get('axisController')

  if (!axisController || !isObject(axisController.axes)) return
  const { axes, axisCfg } = axisController
  const needUpdateAxes = Object.values(axes).filter(d => d && d.dimType === axisType)

  needUpdateAxes.forEach((axis) => {
    const { labels, name, backContainer } = axis
    const { showAllLabel } = (axisCfg && axisCfg[name]) || {}

    labels.sort((a, b) => a._attrs.value - b._attrs.value).forEach((label) => {
      let labelTranslateY = 0
      if (Array.isArray(beforeMatrix) && Array.isArray(afterMatrix)) {
        const labelY = label.get('attrs').y
        labelTranslateY = afterMatrix[5] - beforeMatrix[5] + ((afterMatrix[3] - beforeMatrix[3]) * labelY)
      }
      moveLabel(chart, label, labels, labelTranslateY, { resizeLabel, showAllLabel }, animateConfig, 'y')
    })

    const ticks = backContainer.getChildren().filter(d => d._id === 'axis-x-ticks')
    ticks.forEach((tick) => {
      let tickTranslateY = 0
      if (Array.isArray(beforeMatrix) && Array.isArray(afterMatrix)) {
        const tickY = tick.get('attrs').y2
        tickTranslateY = afterMatrix[5] - beforeMatrix[5] + ((afterMatrix[3] - beforeMatrix[3]) * tickY)
      }
      moveLabel(chart, tick, ticks, tickTranslateY, { resizeLabel, showAllLabel: true }, animateConfig, 'y')
    })
  })
}

/**
 * 对 container 进行缩放，同时需要根据中心点偏移
 * @param container
 * @param scale
 * @param center
 * @param prevCenter
 * @param coord
 * @param chart
 * @param cb
 */
const scaleWithCenter = (container, scale, center, prevCenter, coord, chart, cb) => {
  const matrix = [...container.getMatrix()]
  matrix[4] = (isNumber(matrix[4]) ? matrix[4] : 0)
  const out = matrix
  const m = [...matrix]

  const { start } = coord.x

  out[0] = scale[0]
  out[1] = m[1] * scale[0] // todo 是否需要修改
  out[2] = m[2] * scale[1] // todo
  out[3] = scale[1]

  // x 轴需要同步进行偏移
  // out[4] = m[4] + (m[0] * (1 - diff) * (center.x - start))
  // out[4] = -(((-m[4] + prevCenter.x - start) * diff) - (center.x - start))
  // out[4] = (m[4] * diff) - ((prevCenter.x - start) * diff) + (center.x - start)
  out[4] = ((center.x - start) * (m[0] - out[0])) + m[4]

  const box = container.getBBox()
  box.x = isNumber(box.x) ? coord.x.start : box.x
  box.width = isNumber(box.x) ? coord.x.end - coord.x.start : box.width
  const gap = -box.x * (out[0] - 1)
  // x 轴偏移的边界, 避免溢出
  out[4] = Math.min(gap, Math.max(out[4], (-box.width * (out[0] - 1)) + gap))
  // todo y轴
  out[5] = m[5]

  const tooltipController = chart.get('tooltipController')
  const { crosshairsShapeY } = (tooltipController && tooltipController.tooltip) || {}

  // 移动 tooltip 指示器
  const tooltipX = crosshairsShapeY ? crosshairsShapeY.get('x') : 0
  const translateX = out[4] - m[4] + ((out[0] - m[0]) * tooltipX)
  moveCrosshairsShapeY(crosshairsShapeY, translateX, coord, cb, ZOOM_EASING, ZOOM_SPEED)

  // 移动 x 轴标签
  updateAxisLabel(chart, m, out, true, { easing: ZOOM_EASING, duration: ZOOM_SPEED })

  const finalMatrix = [...out]
  const labelContainer = container.get('parent').get('labelContainer')
  const children = (labelContainer && labelContainer.getChildren()) || []

  children.forEach((child) => {
    const textX = child.get('attrs').x
    const textTranslateX = out[4] - m[4] + ((out[0] - m[0]) * textX)
    moveLabel(chart, child, children, textTranslateX, { isValueLabel: true }, { easing: ZOOM_EASING, duration: ZOOM_SPEED })
  })

  // 新版值标签
  const valueLabelController = chart.get('valueLabel')
  valueLabelController && valueLabelController.update(m, out, true, { duration: ZOOM_SPEED, easing: ZOOM_EASING }, true)

  container.animate().to({
    attrs: {
      matrix: out,
    },
    duration: ZOOM_SPEED,

    easing: ZOOM_EASING,
  }).onEnd(() => {
    container.setMatrix(finalMatrix)
    valueLabelController && valueLabelController.onUpdated()
  })
}

/**
 * 对 container 进行缩放，同时需要根据中心点偏移
 * @param container
 * @param scale
 * @param center
 * @param prevCenter
 * @param coord
 * @param chart
 * @param cb
 */
const scaleWithCenterY = (container, scale, center, prevCenter, coord, chart, cb) => {
  const matrix = [...container.getMatrix()]
  const finalMatrix = matrix
  const tmpMatrix = [...matrix]

  const { end } = coord.y

  // finalMatrix[0] = scale[0]
  finalMatrix[1] = tmpMatrix[1] * scale[0] // todo 是否需要修改
  finalMatrix[2] = tmpMatrix[2] * scale[1] // todo
  finalMatrix[3] = scale[1]

  // x 轴同步偏移
  // finalMatrix[4] = ((center.x - start) * (tmpMatrix[0] - finalMatrix[0])) + tmpMatrix[4]

  // y 轴同步偏移
  finalMatrix[5] = ((center.y - end) * (tmpMatrix[3] - finalMatrix[3])) + tmpMatrix[4]

  const box = container.getBBox()
  const gap = -box.y * (finalMatrix[3] - 1)
  // x 轴偏移的边界, 避免溢出
  // finalMatrix[4] = Math.min(gap, Math.max(finalMatrix[4], (-box.width * (finalMatrix[0] - 1)) + gap))
  // y 轴偏移的边界, 避免溢出
  finalMatrix[5] = Math.min(gap, Math.max(finalMatrix[5], (-box.height * (finalMatrix[3] - 1)) + gap))

  const tooltipController = chart.get('tooltipController')
  const { crosshairsShapeX } = (tooltipController && tooltipController.tooltip) || {}

  // 移动 tooltip 指示器
  const tooltipY = crosshairsShapeX ? crosshairsShapeX.get('y') : 0
  const translateY = finalMatrix[5] - tmpMatrix[5] + ((finalMatrix[3] - tmpMatrix[3]) * tooltipY)
  moveCrosshairsShapeX(crosshairsShapeX, translateY, coord, cb, ZOOM_EASING, ZOOM_SPEED)

  // 移动 y 轴标签
  updateAxisLabelY(chart, tmpMatrix, finalMatrix, true, { easing: ZOOM_EASING, duration: ZOOM_SPEED })

  // 新版值标签
  const valueLabelController = chart.get('valueLabel')
  valueLabelController && valueLabelController.update(tmpMatrix, finalMatrix, true, { duration: ZOOM_SPEED, easing: ZOOM_EASING }, true)
  // console.log(finalMatrix, scale, tmpMatrix, box, gap)
  container.animate().to({
    attrs: {
      matrix: [...finalMatrix],
    },
    duration: ZOOM_SPEED,
    easing: ZOOM_EASING,
  }).onEnd(() => {
    container.setMatrix(finalMatrix)
    // valueLabelController && valueLabelController.onUpdated()
  })
}

/**
 * 对 container 平移
 * @param container
 * @param distance
 * @param coord
 * @param chart
 * @param cb
 */
const move = (container, distance, coord, chart, cb) => {
  const matrix = [...container.getMatrix()]
  matrix[4] = (isNumber(matrix[4]) ? matrix[4] : 0)
  const out = matrix
  const m = [...matrix]

  out[0] = m[0]
  out[1] = m[1]
  out[2] = m[2]
  out[3] = m[3]
  out[4] = m[4] + (1 * distance[0]) + (m[2] * distance[1])
  out[5] = m[5] + (m[1] * distance[0]) + (1 * distance[1])

  const box = container.getBBox()
  box.x = isNumber(box.x) ? coord.x.start : box.x
  box.width = isNumber(box.x) ? coord.x.end - coord.x.start : box.width
  const gap = -box.x * (out[0] - 1)

  // x 轴偏移的边界, 避免溢出
  out[4] = Math.min(gap, Math.max(out[4], (-box.width * (m[0] - 1)) + gap))

  const translateX = out[4] - m[4]

  const tooltipController = chart.get('tooltipController')
  const { crosshairsShapeY } = (tooltipController && tooltipController.tooltip) || {}
  moveCrosshairsShapeY(crosshairsShapeY, translateX, coord, cb)
  // 移动 x 轴标签
  updateAxisLabel(chart, m, out)


  // 移动值标签
  const labelContainer = container.get('parent').get('labelContainer')
  const children = (labelContainer && labelContainer.getChildren()) || []

  children.forEach((child) => {
    const textTranslateX = out[4] - m[4]
    moveLabel(chart, child, children, textTranslateX, { isValueLabel: true }, { easing: MOVE_EASING, duration: MOVE_SPEED })
  })

  // 新版值标签
  const valueLabelController = chart.get('valueLabel')
  valueLabelController && valueLabelController.update(m, out, false, { duration: MOVE_SPEED, easing: MOVE_EASING })

  const finalMatrix = [...out]
  container.animate().to({
    attrs: {
      matrix: out,
    },
    duration: MOVE_SPEED,
    easing: MOVE_EASING,
  }).onEnd(() => {
    container.setMatrix(finalMatrix)
  })
}

/**
 * 对 container 平移
 * @param container
 * @param distance
 * @param coord
 * @param chart
 * @param cb
 */
const moveY = (container, distance, coord, chart, cb) => {
  const matrix = [...container.getMatrix()]
  matrix[5] = (isNumber(matrix[5]) ? matrix[5] : 0)
  const finalMatrix = matrix
  const tmpMatrix = [...matrix]

  finalMatrix[0] = tmpMatrix[0]
  finalMatrix[1] = tmpMatrix[1]
  finalMatrix[2] = tmpMatrix[2]
  finalMatrix[3] = tmpMatrix[3]
  finalMatrix[4] = tmpMatrix[4] + (1 * distance[0]) + (tmpMatrix[2] * distance[1])
  finalMatrix[5] = tmpMatrix[5] + (tmpMatrix[1] * distance[0]) + (1 * distance[1])

  const box = container.getBBox()
  box.y = isNumber(box.y) ? coord.y.end : box.y
  box.height = isNumber(box.y) ? coord.y.start - coord.y.end : box.height
  const gap = -box.y * (finalMatrix[3] - 1)

  // x 轴偏移的边界, 避免溢出
  // finalMatrix[4] = Math.min(gap, Math.max(finalMatrix[4], (-box.width * (tmpMatrix[0] - 1)) + gap))
  // y 轴偏移的边界, 避免溢出
  finalMatrix[5] = Math.min(gap, Math.max(finalMatrix[5], (-box.height * (finalMatrix[3] - 1)) + gap))

  const tooltipController = chart.get('tooltipController')
  const { crosshairsShapeX } = (tooltipController && tooltipController.tooltip) || {}

  // 移动 tooltip 指示器
  const translateY = finalMatrix[5] - tmpMatrix[5]
  moveCrosshairsShapeX(crosshairsShapeX, translateY, coord, cb)

  //移动 y 轴标签
  updateAxisLabelY(chart, tmpMatrix, finalMatrix)

  // 新版值标签
  const valueLabelController = chart.get('valueLabel')
  valueLabelController && valueLabelController.update(tmpMatrix, finalMatrix, false, { duration: MOVE_SPEED, easing: MOVE_EASING })

  container.animate().to({
    attrs: {
      matrix: [...finalMatrix],
    },
    duration: MOVE_SPEED,
    easing: MOVE_EASING,
  }).onEnd(() => {
    container.setMatrix(finalMatrix)
  })
}

const parseTransformPoint = ({ x, y }, matrix) => ({
  x: (x - matrix[4]) / matrix[0],
  y: (y - matrix[5]) / matrix[3],
})

/**
 * 获取缩放后的 geoms 所有元素
 * @param chart
 * @param point
 * @return {[]}
 */
const getSnapRecords = (chart, point) => {
  const geoms = chart.get('geoms')

  let data = []
  if (Array.isArray(geoms) && geoms.length > 0) {
    data = geoms.map((geom) => {
      const matrix = geom.get('container').getMatrix()
      return geom.getSnapRecords(parseTransformPoint(point, matrix))
    })
      .reduce((acc, item) => [...acc, ...item], [])
  }
  return data
}

/**
 * 重新计算 clip
 * @param clip
 */
const resetCoordClip = (clip) => {
  const { height, y } = clip.get('attrs')
  clip.attr('height', height + y)
  clip.attr('y', 0)
  return clip
}

export {
  getSnapRecords,
  scaleWithCenter,
  move,
  calculateXLabelState,
  resetCoordClip,
  updateAxisLabel,
  updateAxisLabelY,
  parseTransformPoint,
  scaleWithCenterY,
  moveY,
  calculateLabelState
}
